import { useState } from "react";
import { api, type Session } from "../api";
import { formatTime } from "../components";

export function Account({ session }: { session: Session }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(null);

    // Checked here purely so a typo does not cost a round trip. The server
    // has no opinion about it — it only ever receives one new password.
    if (next !== confirm) {
      setError("The new passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const result = await api.changeOwnPassword(current, next);
      setDone(
        result.other_sessions_revoked > 0
          ? `Password changed. ${result.other_sessions_revoked} other session(s) were signed out.`
          : "Password changed.",
      );
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Account</h1>
      <p className="subtitle">Your sign-in details.</p>

      <div className="card">
        <h2>Who you are</h2>
        <table>
          <tbody>
            <tr>
              <td style={{ width: 160 }}>Username</td>
              <td className="mono">{session.user.username}</td>
            </tr>
            <tr>
              <td>Role</td>
              <td>
                <span className="badge">{session.user.role}</span>
              </td>
            </tr>
            <tr>
              <td>Permissions</td>
              <td className="mono dim">{session.permissions.join(", ")}</td>
            </tr>
            <tr>
              <td>Account created</td>
              <td className="dim">{formatTime(session.user.created_at)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Change password</h2>
        {error && <div className="notice error">{error}</div>}
        {done && <div className="notice">{done}</div>}

        <form onSubmit={submit} style={{ maxWidth: 340 }}>
          <label>
            Current password
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            New password
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
            <span className="dim">At least 12 characters. A passphrase is fine.</span>
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Changing…" : "Change password"}
          </button>
          <p className="dim" style={{ marginBottom: 0 }}>
            Your other devices will be signed out. This one stays signed in.
          </p>
        </form>
      </div>
    </>
  );
}
